// The print notice tells the reader a print can take up to 60 seconds, so the
// host is asked often through that minute and less after it. The host gives up
// on a job after about 13 minutes and reports it failed, so 15 covers a short
// queue too.
export const HOST_JOB_POLLING = {
    fastIntervalMs: 3000,
    fastWindowMs: 60000,
    slowIntervalMs: 10000,
    giveUpAfterMs: 15 * 60000,
    maxConsecutiveErrors: 3,
};

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Asks the host about a job until it is no longer pending. The first ask goes
 * out at once, since the host may hand back a job that is settled from the
 * start. A failed ask is retried, since one lost request should not cost the
 * reader the job, and only a run of them is thrown.
 *
 * @template {{status: string}} Job
 * @param {() => Promise<Job>} readJob - Asks the host where the job stands
 * @returns {Promise<Job|null>} The settled job, or null when it did not settle in time
 */
export default async function waitForHostJob(readJob) {
    const startedAt = Date.now();
    let consecutiveErrors = 0;

    for (let elapsed = 0; elapsed < HOST_JOB_POLLING.giveUpAfterMs; elapsed = Date.now() - startedAt) {
        try {
            const job = await readJob();
            consecutiveErrors = 0;
            if (job.status !== 'pending') {
                return job;
            }
        } catch (error) {
            consecutiveErrors += 1;
            if (consecutiveErrors >= HOST_JOB_POLLING.maxConsecutiveErrors) {
                throw error;
            }
        }

        await wait(
            elapsed < HOST_JOB_POLLING.fastWindowMs ? HOST_JOB_POLLING.fastIntervalMs : HOST_JOB_POLLING.slowIntervalMs
        );
    }

    return null;
}
